import { Helmet } from 'react-helmet-async';
import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, ChevronLeft, ArrowRight, Settings, Truck, Shield, Star, TrendingUp, Sparkles } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import ProductCard from '../components/ProductCard';
import axios from 'axios';

import { productUrl } from '../lib/seo';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const HomePage = () => {
  const [categories, setCategories] = useState([]);
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [carouselProducts, setCarouselProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentSlide, setCurrentSlide] = useState(0);

  useEffect(() => {
    fetchData();
  }, []);

  // Auto-slide carousel every 4 seconds
  useEffect(() => {
    if (carouselProducts.length === 0) return;
    const totalSlides = Math.ceil(carouselProducts.length / 6);
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % totalSlides);
    }, 4000);
    return () => clearInterval(interval);
  }, [carouselProducts]);

  const fetchData = async () => {
    try {
      const [catRes, prodRes] = await Promise.all([
        axios.get(`${API}/categories`),
        axios.get(`${API}/products/featured?limit=30`)
      ]);
      setCategories(catRes.data);
      
      // Tüm ürünleri al
      const allProducts = prodRes.data || [];
      
      // Carousel için: ürünleri karıştır ve ilk 18'ini al
      const shuffled = [...allProducts].sort(() => Math.random() - 0.5);
      setCarouselProducts(shuffled.slice(0, 18));
      
      // Öne çıkanlar için: ilk 12 ürün (karıştırılmamış)
      setFeaturedProducts(allProducts.slice(0, 12));
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const categoryImages = {
    'fren-sistemi': 'https://customer-assets.emergentagent.com/job_6263b231-3801-4193-99d0-2396d33252b3/artifacts/glebjron_image.png',
    'aydinlatma-sistemi': 'https://customer-assets.emergentagent.com/job_6263b231-3801-4193-99d0-2396d33252b3/artifacts/gj3p95mn_image.png',
    'cam-dis-aksesuar': 'https://customer-assets.emergentagent.com/job_6263b231-3801-4193-99d0-2396d33252b3/artifacts/jvn4emez_image.png',
    'motor-sistemi': 'https://customer-assets.emergentagent.com/job_6263b231-3801-4193-99d0-2396d33252b3/artifacts/3rqst5tu_image.png',
    'hava-emme-turbo-sistemi': 'https://customer-assets.emergentagent.com/job_6263b231-3801-4193-99d0-2396d33252b3/artifacts/qgxcknhr_image.png',
    'yakit-sistemi': 'https://customer-assets.emergentagent.com/job_6263b231-3801-4193-99d0-2396d33252b3/artifacts/l55ojtbz_image.png',
    'egzoz-emisyon': 'https://customer-assets.emergentagent.com/job_6263b231-3801-4193-99d0-2396d33252b3/artifacts/gztwybeu_image.png',
    'elektrik-elektronik': 'https://customer-assets.emergentagent.com/job_6263b231-3801-4193-99d0-2396d33252b3/artifacts/ifbrwx99_image.png',
    'kaporta-parcalari': 'https://customer-assets.emergentagent.com/job_6263b231-3801-4193-99d0-2396d33252b3/artifacts/e4uozgy1_image.png',
    'klima-havalandirma': 'https://customer-assets.emergentagent.com/job_6263b231-3801-4193-99d0-2396d33252b3/artifacts/ikniv600_image.png',
    'bakim-urunleri': 'https://customer-assets.emergentagent.com/job_6263b231-3801-4193-99d0-2396d33252b3/artifacts/c5hv31r3_image.png',
    'diger-urunler': 'https://customer-assets.emergentagent.com/job_6263b231-3801-4193-99d0-2396d33252b3/artifacts/3srr8f9i_image.png'
  };

  const totalSlides = Math.ceil(carouselProducts.length / 6);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % totalSlides);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + totalSlides) % totalSlides);
  };

  return (
    <>
      <Helmet>
        <title>Pazarlo - Türkiye'nin En Büyük Oto Yedek Parça Pazarı | 150.000+ Ürün</title>
        <meta name="description" content="Türkiye'nin en büyük online oto yedek parça pazarı. Fren, motor, süspansiyon, kaporta parçaları. 150.000+ ürün, uygun fiyatlar, hızlı teslimat." />
        <meta name="keywords" content="oto yedek parça, araba parçası, fren balatası, motor parçası, süspansiyon, kaporta, araç parçası" />
        <link rel="canonical" href="https://pazarlo.com/" />
        <meta property="og:title" content="Pazarlo - Oto Yedek Parça Pazarı" />
        <meta property="og:description" content="150.000+ oto yedek parça. Uygun fiyat, hızlı teslimat." />
        <meta property="og:url" content="https://pazarlo.com/" />
        <meta property="og:type" content="website" />
      </Helmet>
      <div className="min-h-screen" data-testid="home-page">
      {/* Stats Section */}
      <section className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 py-8 relative">
        <div className="container mx-auto px-4">
          <div className="flex flex-wrap justify-center gap-8">
            <div className="text-center">
              <div className="text-3xl font-bold text-orange-400">10K+</div>
              <div className="text-slate-400 text-sm">Ürün</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-orange-400">500+</div>
              <div className="text-slate-400 text-sm">Satıcı</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-orange-400">50K+</div>
              <div className="text-slate-400 text-sm">Müşteri</div>
            </div>
          </div>
        </div>
        <div className="absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M0 120L60 105C120 90 240 60 360 45C480 30 600 30 720 37.5C840 45 960 60 1080 67.5C1200 75 1320 75 1380 75L1440 75V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0Z" fill="hsl(var(--background))"/>
          </svg>
        </div>
      </section>

      {/* Auto-Sliding Product Carousel */}
      {carouselProducts.length > 0 && (
        <section className="py-10 px-4 bg-gradient-to-b from-white to-white">
          <div className="container mx-auto">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <Sparkles className="h-6 w-6 text-orange-500" />
                <h2 className="text-2xl font-bold text-slate-900">Yeni Ürünler</h2>
              </div>
              <div className="flex items-center gap-2">
                <button 
                  onClick={prevSlide}
                  className="p-2 rounded-full bg-white border border-slate-200 hover:bg-slate-50 transition-colors"
                >
                  <ChevronLeft className="h-5 w-5 text-slate-600" />
                </button>
                <button 
                  onClick={nextSlide}
                  className="p-2 rounded-full bg-white border border-slate-200 hover:bg-slate-50 transition-colors"
                >
                  <ChevronRight className="h-5 w-5 text-slate-600" />
                </button>
                <Link to="/products" className="ml-4 text-orange-500 hover:text-orange-600 font-medium flex items-center gap-1">
                  Tümünü Gör
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
            
            {/* Carousel Container */}
            <div className="relative overflow-hidden">
              <div 
                className="flex transition-transform duration-500 ease-in-out"
                style={{ transform: `translateX(-${currentSlide * 100}%)` }}
              >
                {Array.from({ length: totalSlides }).map((_, slideIndex) => (
                  <div key={slideIndex} className="min-w-full grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
                    {carouselProducts.slice(slideIndex * 6, (slideIndex + 1) * 6).map((product) => (
                      <Link 
                        key={product.id} 
                        to={productUrl(product)}
                        className="group bg-white rounded-xl shadow-sm hover:shadow-lg transition-all duration-300 overflow-hidden border border-slate-100"
                      >
                        <div className="aspect-square bg-white relative overflow-hidden">
                          {product.image || product.image_url ? (
                            <img 
                              src={product.image || product.image_url} 
                              alt={product.name}
                              className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-300"
                              onError={(e) => {
                                e.target.style.display = 'none';
                                if(e.target.nextSibling) e.target.nextSibling.style.display = 'flex';
                              }}
                            />
                          ) : null}
                          <div className={`absolute inset-0 flex items-center justify-center bg-white ${product.image || product.image_url ? 'hidden' : ''}`}>
                            <span className="text-3xl font-bold text-slate-400">{product.brand?.charAt(0) || '?'}</span>
                          </div>
                          {product.stock > 0 && (
                            <span className="absolute top-2 right-2 bg-green-500 text-white text-xs px-2 py-0.5 rounded-full">
                              Stokta
                            </span>
                          )}
                        </div>
                        <div className="p-3">
                          <div className="text-xs text-slate-500 mb-1">{product.oem_code}</div>
                          {product.brand && (<div className="mb-1"><span className="text-xs font-medium bg-slate-100 text-slate-700 px-2 py-0.5 rounded">{product.brand}</span></div>)}
                          <h3 className="text-sm font-medium text-slate-800 line-clamp-3 group-hover:text-orange-500 transition-colors min-h-[60px]">
                            {product.name}
                          </h3>
                          <div className="mt-2 pt-2 border-t border-slate-100">
                            <span className="text-lg font-bold text-slate-900">
                              {(product.display_price || product.price || 0).toLocaleString('tr-TR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} TL
                            </span>
                          </div>
                        </div>
                      </Link>
                    ))}
                  </div>
                ))}
              </div>
              
              {/* Slide Indicators */}
              <div className="flex justify-center gap-2 mt-4">
                {Array.from({ length: totalSlides }).map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentSlide(index)}
                    className={`w-2 h-2 rounded-full transition-all ${currentSlide === index ? 'bg-orange-500 w-6' : 'bg-slate-300'}`}
                  />
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      <section className="py-12 px-4 bg-slate-50">
        <div className="container mx-auto">
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center gap-3">
              <TrendingUp className="h-6 w-6 text-orange-500" />
              <h2 className="text-2xl font-bold text-slate-900">Öne Çıkan Ürünler</h2>
            </div>
            <Link to="/products" className="text-orange-500 hover:text-orange-600 flex items-center gap-1 text-sm font-medium">
              Tümünü Gör <ChevronRight className="h-4 w-4" />
            </Link>
          </div>

          {loading ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-4">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="bg-white rounded-xl border p-4 animate-pulse">
                  <div className="aspect-square bg-slate-200 rounded-lg mb-4" />
                  <div className="h-4 bg-slate-200 rounded mb-2" />
                  <div className="h-4 bg-slate-200 rounded w-2/3" />
                </div>
              ))}
            </div>
          ) : featuredProducts.length > 0 ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-4">
              {featuredProducts.map((product) => (
                <ProductCard key={product.id} product={product} />
              ))}
            </div>
          ) : (
            <Card className="p-12 text-center">
              <CardContent>
                <Settings className="h-16 w-16 text-slate-300 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-slate-600 mb-2">Henüz ürün yok</h3>
                <p className="text-slate-500 mb-4">İlk satıcı siz olun!</p>
                <Link to="/seller">
                  <Button className="bg-orange-500 hover:bg-orange-600">
                    Satıcı Ol
                  </Button>
                </Link>
              </CardContent>
            </Card>
          )}
        </div>
      </section>

      {/* Why Us Section */}
      <section className="py-16 px-4">
        <div className="container mx-auto">
          <h2 className="text-2xl md:text-3xl font-bold text-center text-slate-900 mb-12">
            Neden Pazarlo?
          </h2>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center p-6">
              <div className="w-16 h-16 bg-orange-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Shield className="h-8 w-8 text-orange-500" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-2">Güvenli Alışveriş</h3>
              <p className="text-slate-600">
                256-bit SSL şifreleme ve güvenli ödeme altyapısı ile paranız güvende.
              </p>
            </div>
            
            <div className="text-center p-6">
              <div className="w-16 h-16 bg-orange-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Star className="h-8 w-8 text-orange-500" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-2">Kaliteli Ürünler</h3>
              <p className="text-slate-600">
                Doğrulanmış satıcılar ve müşteri yorumları ile en iyi ürünleri bulun.
              </p>
            </div>
            
            <div className="text-center p-6">
              <div className="w-16 h-16 bg-orange-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Truck className="h-8 w-8 text-orange-500" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-2">Hızlı Kargo</h3>
              <p className="text-slate-600">
                Türkiye'nin her yerine 1-3 iş gününde teslimat.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-4 bg-gradient-to-r from-orange-500 to-orange-600">
        <div className="container mx-auto text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
            Satıcı Olmak İster misiniz?
          </h2>
          <p className="text-orange-100 mb-8 max-w-2xl mx-auto">
            Mağazanızı açın, ürünlerinizi listeleyin ve Türkiye'nin dört bir yanındaki müşterilere ulaşın. 
            %10 komisyon oranıyla kazancınızı artırın.
          </p>
          <Link to="/seller" data-testid="cta-seller-btn">
            <Button size="lg" variant="secondary" className="text-orange-600 bg-white hover:bg-orange-50 text-lg px-8">
              Hemen Başla
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
    </>
  );
};

export default HomePage;
